import { useEffect, useState } from "react";
import { ThumbsUp, ThumbsDown } from "lucide-react";
import { submitFeedback } from "@/lib/api";

const STORAGE_PREFIX = "sg_fb::";

function localKey(caseId, itemType, itemId) {
  return `${STORAGE_PREFIX}${caseId}::${itemType}::${itemId}`;
}

/**
 * Silent 👍/👎 for a single item. Stores locally so refresh persists the visual
 * state and posts to /api/feedback (upsert on case_id + item_type + item_id).
 */
export default function FeedbackButtons({
  caseId,
  itemType,
  itemId,
  itemText,
  themeLabel,
  problemType,
  size = "sm",
  align = "end",
  disabled,
}) {
  const [vote, setVote] = useState(null);

  useEffect(() => {
    if (!caseId) return;
    const v = localStorage.getItem(localKey(caseId, itemType, itemId));
    if (v === "up" || v === "down") setVote(v);
    else setVote(null);
  }, [caseId, itemType, itemId]);

  const onClick = async (next) => {
    if (disabled || !caseId) return;
    const toggled = vote === next ? null : next;
    setVote(toggled);
    try {
      localStorage.setItem(localKey(caseId, itemType, itemId), toggled || "");
      await submitFeedback({
        case_id: caseId,
        item_type: itemType,
        item_id: itemId,
        item_text: itemText,
        theme_label: themeLabel,
        problem_type: problemType,
        vote: toggled,
      });
    } catch (_) {
      // silent — never interrupt the user
    }
  };

  const btn = size === "md" ? "h-8 w-8" : "h-6 w-6";
  const icon = size === "md" ? "h-4 w-4" : "h-3.5 w-3.5";

  return (
    <div
      className={`inline-flex items-center gap-1 ${
        align === "start" ? "" : "ml-auto"
      }`}
      data-testid={`feedback-${itemType}-${itemId}`}
    >
      <button
        type="button"
        onClick={() => onClick("up")}
        disabled={disabled}
        aria-label="Helpful"
        aria-pressed={vote === "up"}
        data-testid={`feedback-${itemType}-${itemId}-up`}
        className={`${btn} inline-flex items-center justify-center rounded-full border transition-[background-color,border-color,color] ${
          vote === "up"
            ? "bg-[#35D07F] border-[#35D07F] text-black"
            : "bg-transparent border-white/15 text-white/50 hover:text-white hover:border-white/30"
        } disabled:opacity-40`}
      >
        <ThumbsUp className={icon} strokeWidth={vote === "up" ? 2.5 : 2} />
      </button>
      <button
        type="button"
        onClick={() => onClick("down")}
        disabled={disabled}
        aria-label="Not helpful"
        aria-pressed={vote === "down"}
        data-testid={`feedback-${itemType}-${itemId}-down`}
        className={`${btn} inline-flex items-center justify-center rounded-full border transition-[background-color,border-color,color] ${
          vote === "down"
            ? "bg-[#FF5252] border-[#FF5252] text-black"
            : "bg-transparent border-white/15 text-white/50 hover:text-white hover:border-white/30"
        } disabled:opacity-40`}
      >
        <ThumbsDown className={icon} strokeWidth={vote === "down" ? 2.5 : 2} />
      </button>
    </div>
  );
}
